import { useCallback, useEffect, useMemo, useState } from "react"
import { DateTime } from "luxon"
import Highcharts from "highcharts"
import HighchartsReact from "highcharts-react-official"
import DateFilter, {
  DateRangePreset
} from "../../common/ui/activity/date-filter"
import { ListSkeleton } from "../../common/ui/skeletons"
import bbApi from "../../api"
import {
  ApiKeyUsageByKey,
  ApiKeyUsageByEndpoint,
  ApiKeyUsageDaily
} from "../../api/api-usage"

interface ApiUsageSectionProps {
  initialKeyId?: string
}

const ApiUsageSection = ({ initialKeyId = "" }: ApiUsageSectionProps) => {
  const [dateRange, setDateRange] = useState<DateRangePreset>("last_30")
  const [customStartDate, setCustomStartDate] = useState<Date | null>(null)
  const [customEndDate, setCustomEndDate] = useState<Date | null>(null)

  // Key filter
  const [selectedKeyId, setSelectedKeyId] = useState<string>(initialKeyId)

  // Daily data (chart)
  const [dailyData, setDailyData] = useState<ApiKeyUsageDaily[]>([])
  const [isLoadingDaily, setIsLoadingDaily] = useState(true)

  // Key-level usage data
  const [keyData, setKeyData] = useState<ApiKeyUsageByKey[]>([])
  const [isLoadingKeys, setIsLoadingKeys] = useState(true)

  // Selected day from chart click
  const [selectedDay, setSelectedDay] = useState<string | null>(null)

  // Endpoint data for selected day drill-down
  const [endpointData, setEndpointData] = useState<ApiKeyUsageByEndpoint[]>([])
  const [isLoadingEndpoints, setIsLoadingEndpoints] = useState(false)

  const days = useMemo(() => {
    switch (dateRange) {
      case "today":
        return 1
      case "last_7":
        return 7
      case "last_30":
        return 30
      case "last_90":
        return 90
      case "custom":
        if (customStartDate && customEndDate) {
          const start = DateTime.fromJSDate(customStartDate)
          const end = DateTime.fromJSDate(customEndDate)
          return Math.ceil(end.diff(start, "days").days)
        }
        return 30
      case "all":
      default:
        return 365
    }
  }, [dateRange, customStartDate, customEndDate])

  const dailyParams = useMemo(() => {
    const p: Record<string, string | number | boolean | undefined> = { days }
    if (selectedKeyId) p.api_key_id = selectedKeyId
    return p
  }, [days, selectedKeyId])

  // Fetch daily + key-level usage
  useEffect(() => {
    let cancelled = false

    const fetchDaily = async () => {
      setIsLoadingDaily(true)
      try {
        const data = await bbApi.apiUsage.getDaily(dailyParams)
        if (!cancelled) {
          setDailyData(data)
          setSelectedDay(null)
        }
      } catch {
        if (!cancelled) setDailyData([])
      } finally {
        if (!cancelled) setIsLoadingDaily(false)
      }
    }

    fetchDaily()

    return () => {
      cancelled = true
    }
  }, [dailyParams])

  // Fetch key-level usage data
  useEffect(() => {
    let cancelled = false
    const fetchKeys = async () => {
      setIsLoadingKeys(true)
      try {
        const data = await bbApi.apiUsage.getByKey({ days })
        if (!cancelled) setKeyData(data)
      } catch {
        if (!cancelled) setKeyData([])
      } finally {
        if (!cancelled) setIsLoadingKeys(false)
      }
    }
    fetchKeys()
    return () => {
      cancelled = true
    }
  }, [days])

  // Fetch endpoint breakdown when a day is clicked
  useEffect(() => {
    if (!selectedDay) {
      setEndpointData([])
      return
    }
    let cancelled = false
    const fetchEndpoints = async () => {
      setIsLoadingEndpoints(true)
      try {
        // Fetch endpoint breakdown for a 1-day window around the selected day
        const dayDt = DateTime.fromISO(selectedDay)
        const today = DateTime.now().startOf("day")
        const daysBack = Math.max(
          1,
          Math.ceil(today.diff(dayDt, "days").days) + 1
        )

        const params: Record<string, string | number | boolean | undefined> = {
          days: daysBack,
          api_key_id: selectedKeyId || undefined
        }
        const data = await bbApi.apiUsage.getByEndpoint(params)
        if (!cancelled) setEndpointData(data)
      } catch {
        if (!cancelled) setEndpointData([])
      } finally {
        if (!cancelled) setIsLoadingEndpoints(false)
      }
    }
    fetchEndpoints()
    return () => {
      cancelled = true
    }
  }, [selectedDay, selectedKeyId])

  const handleDateRangeChange = useCallback(
    (range: DateRangePreset, startDate?: Date, endDate?: Date) => {
      setDateRange(range)
      setCustomStartDate(startDate || null)
      setCustomEndDate(endDate || null)
      setSelectedDay(null)
    },
    []
  )

  const totalHits = useMemo(
    () => keyData.reduce((sum, row) => sum + (parseInt(row.hits, 10) || 0), 0),
    [keyData]
  )

  // Sorted daily data for chart
  const sortedDaily = useMemo(
    () =>
      [...dailyData]
        .map(d => ({
          day: d.day,
          date: DateTime.fromISO(d.day),
          hits: parseInt(d.hits, 10) || 0,
          key_count: d.key_count,
          endpoint_count: d.endpoint_count
        }))
        .sort((a, b) => (a.date < b.date ? -1 : 1)),
    [dailyData]
  )

  // The selected day's data
  const selectedDayData = useMemo(
    () => (selectedDay ? sortedDaily.find(d => d.day === selectedDay) : null),
    [selectedDay, sortedDaily]
  )

  // Build chart from daily data
  const chartOptions = useMemo((): Highcharts.Options => {
    const selectedIndex = selectedDay
      ? sortedDaily.findIndex(d => d.day === selectedDay)
      : -1

    return {
      chart: { type: "column", height: 280 },
      title: { text: "" },
      credits: { enabled: false },
      xAxis: {
        type: "category",
        categories: sortedDaily.map(d => d.date.toFormat("LLL dd")),
        labels: {
          rotation: sortedDaily.length > 15 ? -45 : 0,
          style: { fontSize: "11px", color: "#A3A3A9" }
        }
      },
      yAxis: {
        min: 0,
        title: { text: "Hits" },
        lineWidth: 1
      },
      legend: { enabled: false },
      tooltip: {
        backgroundColor: "#42405A",
        borderColor: "#42405A",
        borderRadius: 5,
        shadow: false,
        style: { color: "#ffffff", fontSize: "12px" },
        headerFormat: '<span style="font-weight: 700">{point.key}</span><br/>',
        pointFormat:
          '<span>{point.y} hits</span><br/><span style="font-size:11px;opacity:0.7">Click to view details</span>'
      },
      plotOptions: {
        column: {
          cursor: "pointer",
          borderWidth: 0,
          borderRadius: 3,
          point: {
            events: {
              click: function () {
                const dayStr = sortedDaily[this.index]?.day
                if (dayStr)
                  setSelectedDay(prev => (prev === dayStr ? null : dayStr))
              }
            }
          }
        }
      },
      series: [
        {
          type: "column",
          name: "Hits",
          color: "#786ff6",
          data: sortedDaily.map((d, i) => ({
            y: d.hits,
            color:
              selectedIndex === -1
                ? "#786ff6"
                : i === selectedIndex
                ? "#5016a3"
                : "rgba(120, 111, 246, 0.3)"
          }))
        }
      ]
    }
  }, [sortedDaily, selectedDay])

  const TH_CLASS =
    "px-3 py-2 text-left uppercase text-neutral-400 text-xs font-medium"
  const TD_CLASS = "px-3 py-2 text-sm"

  return (
    <div className="space-y-6">
      {/* Header row */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          {!isLoadingKeys && keyData.length > 0 && (
            <p className="text-sm text-neutral-500">
              {totalHits.toLocaleString()} total hits across {keyData.length}{" "}
              key{keyData.length !== 1 ? "s" : ""}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          {/* Key selector */}
          <select
            value={selectedKeyId}
            onChange={e => {
              setSelectedKeyId(e.target.value)
              setSelectedDay(null)
            }}
            className="text-sm border border-neutral-200 rounded-md pl-3 pr-8 py-2 bg-white text-neutral-700 focus:outline-none focus:ring-1 focus:ring-primary-400 focus:border-primary-400 appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23737373%22%20stroke-width%3D%222%22%3E%3Cpath%20d%3D%22M6%209l6%206%206-6%22%2F%3E%3C%2Fsvg%3E')] bg-[length:12px] bg-[right_8px_center] bg-no-repeat"
          >
            <option value="">All keys</option>
            {keyData.map(k => (
              <option key={k.api_key_id} value={k.api_key_id}>
                ...{k.key_suffix}
                {k.key_name ? ` — ${k.key_name}` : ""}
              </option>
            ))}
          </select>
          <DateFilter
            selectedRange={dateRange}
            customStartDate={customStartDate}
            customEndDate={customEndDate}
            onRangeChange={handleDateRangeChange}
            hideLabelOnMobile
          />
        </div>
      </div>

      {/* Daily usage chart */}
      <div className="bg-white rounded-lg border border-neutral-200 p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-base font-semibold text-neutral-800">
            Daily Usage
          </h3>
          {selectedDay && selectedDayData && (
            <button
              onClick={() => setSelectedDay(null)}
              className="text-xs text-primary-400 hover:text-primary-500 transition-colors"
            >
              Clear selection
            </button>
          )}
        </div>
        {isLoadingDaily ? (
          <div className="animate-pulse">
            <div className="h-[260px] bg-neutral-100 rounded" />
          </div>
        ) : dailyData.length === 0 ? (
          <div className="h-[260px] flex items-center justify-center text-neutral-400">
            No usage data for this period.
          </div>
        ) : (
          <HighchartsReact highcharts={Highcharts} options={chartOptions} />
        )}
      </div>

      {/* Selected day detail */}
      {selectedDay && selectedDayData && (
        <div className="bg-white rounded-lg border border-primary-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-neutral-100 bg-primary-50">
            <h3 className="text-base font-semibold text-neutral-800">
              {selectedDayData.date.toFormat("EEEE, LLL dd yyyy")}
            </h3>
            <div className="flex gap-6 mt-1 text-sm text-neutral-600">
              <span>
                <strong className="font-mono">
                  {selectedDayData.hits.toLocaleString()}
                </strong>{" "}
                hits
              </span>
              <span>
                <strong className="font-mono">
                  {selectedDayData.key_count}
                </strong>{" "}
                keys
              </span>
              <span>
                <strong className="font-mono">
                  {selectedDayData.endpoint_count}
                </strong>{" "}
                endpoints
              </span>
            </div>
          </div>
          {isLoadingEndpoints ? (
            <div className="p-4">
              <ListSkeleton noOfRows={3} noOfColumns={3} />
            </div>
          ) : endpointData.length === 0 ? (
            <div className="px-6 py-6 text-center text-neutral-500">
              No endpoint data available.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-zinc-200">
                    <th className={TH_CLASS}>Endpoint</th>
                    <th className={TH_CLASS}>Method</th>
                    <th className={TH_CLASS}>Hits</th>
                  </tr>
                </thead>
                <tbody>
                  {endpointData.map((row, i) => (
                    <tr
                      key={`${row.endpoint}-${row.request_method}-${i}`}
                      className="border-b border-zinc-200 hover:bg-neutral-50 transition-colors"
                    >
                      <td className={`${TD_CLASS} font-mono text-neutral-700`}>
                        {row.endpoint}
                      </td>
                      <td className={`${TD_CLASS} whitespace-nowrap`}>
                        <span
                          className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                            row.request_method === "GET"
                              ? "bg-green-50 text-green-700"
                              : row.request_method === "POST"
                              ? "bg-blue-50 text-blue-700"
                              : row.request_method === "DELETE"
                              ? "bg-red-50 text-red-700"
                              : "bg-neutral-100 text-neutral-600"
                          }`}
                        >
                          {row.request_method}
                        </span>
                      </td>
                      <td
                        className={`${TD_CLASS} font-mono font-medium text-neutral-800`}
                      >
                        {(parseInt(row.hits, 10) || 0).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default ApiUsageSection
